import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Header, { DecisionTimeHeaderContent } from 'components/Header';
import GameCardContainer from 'components/GameCardContainer';
import PlayerCardWithFade, { PlayerCardWithFadeWidths } from 'components/PlayerCardWithFade';
import { decisionTimeCardsTest } from 'test/testData';
import './DecisionTime.scss';
import RestrictedContainer from 'components/RestrictedContainer';

export interface IDecisionTimeCard {
  playerName: string;
  selectionNumber: string;
  teamCode: string;
  homeTeamCode: string;
  awayTeamCode: string;
  formattedDate: string;
  position: string;
  playerNo: number;
  team: string;
}

interface IAnimatedContent {
  matchDetails: boolean;
  cta: boolean;
}

const DecisionTime = () => {
  const { t } = useTranslation();
  const cards: IDecisionTimeCard[] = decisionTimeCardsTest;
  const ctaRefs = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)];

  // toDo - re-implement this using useReducer
  const [ctaOneAnimatedContent, setCtaOneAnimatedContent] = useState<IAnimatedContent>({ matchDetails: false, cta: false });
  const [ctaTwoAnimatedContent, setCtaTwoAnimatedContent] = useState<IAnimatedContent>({ matchDetails: false, cta: false });

  const getCtaAnimatedContent = (index: number) => (index === 0 ? ctaOneAnimatedContent : ctaTwoAnimatedContent);
  const getSetCtaAnimatedContent = (index: number) => (index === 0 ? setCtaOneAnimatedContent : setCtaTwoAnimatedContent);

  useEffect(() => {
    const refs = [ctaRefs[0].current, ctaRefs[1].current];
    refs.forEach((ref, index) => ref?.addEventListener('animationend', () => handleRenderCtaUpdate(index)));

    return () => {
      refs.forEach((ref, index) => ref?.removeEventListener('animationend', () => handleRenderCtaUpdate(index)));
    };
    // eslint-disable-next-line
  }, [ctaOneAnimatedContent, ctaTwoAnimatedContent]);

  const handleRenderMatchDetailsUpdate = (index: number) => {
    const ctaAnimatedContentState = getCtaAnimatedContent(index);
    const animatedCta = getSetCtaAnimatedContent(index);
    animatedCta({
      ...ctaAnimatedContentState,
      matchDetails: true
    });
  };

  const handleRenderCtaUpdate = (index: number) => {
    const ctaAnimatedContentState = getCtaAnimatedContent(index);
    const animatedCta = getSetCtaAnimatedContent(index);
    animatedCta({
      ...ctaAnimatedContentState,
      cta: true
    });
  };

  const renderCardWithCustomCta = (card: IDecisionTimeCard, index: number) => {
    const ctaAnimatedContentState = getCtaAnimatedContent(index);
    return (
      <div className="decision-time__card-wrapper" key={`"decision-time__card-wrapper--${index}`}>
        <PlayerCardWithFade
          width={PlayerCardWithFadeWidths.oneHundredAndFifty}
          onRevealAnimationEndCallBack={() => handleRenderMatchDetailsUpdate(index)}
          name={card.playerName}
          selectionNumber={card.selectionNumber}
          teamCode={card.teamCode}
          details={{
            team: card.team,
            playerNo: card.playerNo.toString(),
            position: card.position
          }}
        />
        <>
          {ctaAnimatedContentState.matchDetails && (
            <div className="decision-time__match-details" ref={ctaRefs[index]}>
              <div className="decision-time__vs">
                <div className="decision-time__team">{card.homeTeamCode}</div>
                vs
                <div className="decision-time__team">{card.awayTeamCode}</div>
              </div>
              <div className="decision-time__time">{card.formattedDate}</div>
            </div>
          )}
          {ctaAnimatedContentState.cta && <div className={'decision-time__cta'}>{t('pick')}</div>}
        </>
      </div>
    );
  };

  return (
    <RestrictedContainer className="decision-time">
      <Header>
        <DecisionTimeHeaderContent />
      </Header>
      <GameCardContainer>{cards.map((card, index) => renderCardWithCustomCta(card, index))}</GameCardContainer>
    </RestrictedContainer>
  );
};

export default DecisionTime;
